// De publicatiepoort op het EFFECT, zoals het scherm hem leest.
//
// De ECHTE poort staat server-side (functions/intelligence/effectBenchmarkGate.js):
// publiceren gebeurt daar, en een weigering komt daarvandaan. Dit is de
// spiegel voor de weergave — zodat het scherm niet 'klaar om te publiceren'
// zegt over iets dat de server gaat weigeren.
//
// Drie standen, want er zijn twee verschillende manieren om níet door te
// mogen: de benchmark is te zwak, of hij staat nog niet vast. Die op één stand
// boeken laat de poort het goed doen terwijl de diagnose liegt.
// AI-CONTEXT: See docs/pitfalls.md#dev-gate-counter-hides-its-own-policy
//
// 'Nog niet bekend' is nooit groen licht: zolang de benchmark laadt of niet te
// beoordelen is, blijft de poort dicht.
// AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
// AI-CONTEXT: See docs/decisions.md ADR-040

/** De drie standen van de benchmark van één effect. */
export const BENCHMARK_STATE = Object.freeze({
    unknown: 'onbekend',
    tooWeak: 'te-zwak',
    ok: 'gehaald',
})

/**
 * De stand van één effect, uit een benchmarkrij zoals buildBenchmarkOverview
 * hem levert (useEffectBenchmark, of een rij uit het overzicht).
 *
 * @param {{loaded?: boolean, row?: (object|null)}|null} benchmark
 * @returns {string} een van BENCHMARK_STATE
 */
export const effectBenchmarkState = (benchmark) => {
    if (!benchmark || benchmark.loaded !== true) return BENCHMARK_STATE.unknown
    const row = benchmark.row
    // Geen rij, of een rij waarover geen sterkteoordeel te geven is: dat is
    // 'weten we niet', en dus niet 'gehaald'.
    if (!row || row.judgeable !== true) return BENCHMARK_STATE.unknown
    return row.thresholdMet === true ? BENCHMARK_STATE.ok : BENCHMARK_STATE.tooWeak
}

/**
 * Dezelfde stand, maar opzoekbaar per effect — voor schermen die meer dan één
 * effect tonen (de themalijst). Een effect dat niet in de lijst zit is
 * onbekend: de lijst kan het effect niet kennen, en dat is geen groen licht.
 *
 * @param {{loaded?: boolean, rows?: Array<object>}|null} benchmarks
 * @returns {(msiId: string) => string}
 */
export const benchmarkStateByEffect = (benchmarks) => {
    const geladen = !!benchmarks && benchmarks.loaded === true
    const perEffect = new Map()
    if (geladen) {
        (benchmarks.rows || []).forEach((row) => {
            const id = row && (row.effectId || row.rowId)
            if (id) perEffect.set(id, effectBenchmarkState({ loaded: true, row }))
        })
    }
    return (msiId) => perEffect.get(msiId) || BENCHMARK_STATE.unknown
}

/**
 * Waarom er niet gepubliceerd kan worden, in gewone taal. Leeg als het wel kan.
 *
 * @param {string} state - een van BENCHMARK_STATE
 * @returns {string}
 */
export const publicationBlockedReason = (state) => {
    if (state === BENCHMARK_STATE.ok) return ''
    if (state === BENCHMARK_STATE.tooWeak) {
        return 'De databasis van dit effect haalt de drempel nog niet, dus publiceren kan nog niet.'
    }
    return 'De databasis van dit effect is nog niet bekend, dus publiceren kan nog niet.'
}

export default effectBenchmarkState
